import { useAuth } from '@/contexts/auth-context';
import { useBillingActionScope } from '@/hooks/actions/use-billing-action-scope';
import api from '@/libs/axiosInstance';
import { readBillingContext } from '@/libs/billing-access';
import type {
  ApiResponse,
  OrganizationBillingContext,
  OrganizationBillingOffers,
} from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

export const BILLING_OFFERS_QUERY_KEY = ['billingOffers'] as const;

const offersSchema = z
  .object({
    organizationId: z.string().min(1),
    brandId: z.string().min(1),
    currency: z.enum(['EUR', 'USD', 'GBP', 'CAD']).nullable(),
    offers: z.array(
      z
        .object({
          planId: z.enum(['essential_monthly', 'growth_monthly']),
          currency: z.enum(['EUR', 'USD', 'GBP', 'CAD']),
          unitAmount: z.number().finite().int().positive().refine(Number.isSafeInteger),
          interval: z.literal('monthly'),
        })
        .strict()
    ),
  })
  .strict()
  .refine(
    (value) =>
      new Set(value.offers.map((offer) => offer.planId)).size === value.offers.length &&
      value.offers.every((offer) => offer.currency === value.currency)
  );

/** The authenticated catalogue supplies every Stripe price and currency. */
export function useBillingOffersQuery({
  context,
  enabled = true,
}: {
  context?: OrganizationBillingContext;
  enabled?: boolean;
}) {
  const { isLoading } = useAuth();
  const { scope, assertCurrent } = useBillingActionScope();
  const canReadOffers =
    !!context &&
    !!readBillingContext(context) &&
    context.organizationId === scope.organizationId &&
    context.brandId === scope.brandId &&
    context.channel === 'stripe' &&
    context.capabilities.canCommitPaidPlan &&
    context.cardSetup.exemption !== 'impersonation';
  return useQuery<OrganizationBillingOffers>({
    queryKey: [...BILLING_OFFERS_QUERY_KEY, scope.organizationId, scope.brandId],
    enabled: enabled && !isLoading && canReadOffers,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) => {
      assertCurrent(scope);
      if (!canReadOffers) throw new Error('No authorized billing catalogue');
      const response = await api.get<ApiResponse<unknown>>('/dashboard/billing/offers', { signal });
      assertCurrent(scope);
      const result = offersSchema.safeParse(response.data.data);
      if (
        response.data.success !== true ||
        !result.success ||
        result.data.organizationId !== scope.organizationId ||
        result.data.brandId !== scope.brandId ||
        result.data.currency !== context?.billingCurrency
      )
        throw new Error('Invalid billing catalogue');
      return result.data;
    },
  });
}
